import { Injectable, EventEmitter, Injector } from '@angular/core';
import { Router, NavigationEnd, NavigationExtras } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TabInfo, TabPersistenceState } from './tab.model';
import { ROUTE_TAB_CONFIG, TAB_STORAGE_KEY, DEFAULT_ROUTE } from './route-tab.config';
import { PageHistoryService } from '../page-history/page-history.service';

/**
 * Service for managing MDI tabs.
 * Handles opening, closing, switching tabs, and persisting state to localStorage.
 */
@Injectable({ providedIn: 'root' })
export class TabService {
  /** Array of currently open tabs */
  tabs: TabInfo[] = [];

  /** ID of the currently active tab */
  activeTabId: string | null = null;

  /** Event emitted when tabs array changes */
  tabsChanged = new EventEmitter<TabInfo[]>();

  /** Event emitted when active tab changes */
  activeTabChanged = new EventEmitter<string | null>();

  /** Event emitted when a tab's dirty state changes */
  tabDirtyChanged = new EventEmitter<{ tabId: string; dirty: boolean }>();

  private _router: Router | null = null;
  private get router(): Router {
    if (!this._router) {
      this._router = this.injector.get(Router);
    }
    return this._router;
  }

  constructor(private injector: Injector, private pageHistory: PageHistoryService) {
    this.loadFromStorage();
    // Delay subscription to avoid circular dependency during Router initialization
    setTimeout(() => this.setupRouterSubscription());
  }

  /**
   * Subscribe to router events to sync tabs with navigation
   */
  private setupRouterSubscription(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // Re-entrancy is prevented in activateTab() by short-circuiting when the
        // router is already on the tab's route, so every NavigationEnd can be
        // handled. A previous global "isNavigating" guard swallowed concurrent
        // navigations and caused menu clicks to need a second click.
        this.handleNavigation(event.urlAfterRedirects);
      });
  }

  /**
   * Handle navigation events - open or activate tab
   */
  handleNavigation(url: string): void {
    // Parse the URL to get base path and any parameters
    const { basePath, params, isInternalRoute, parentBasePath } = this.parseUrl(url);

    // Check if this route has tab configuration
    const config = this.getConfigForPath(basePath);
    if (!config) {
      return; // Not a tabbed route
    }

    // For internal routes (parameterized routes handled by parent component),
    // find/activate the parent tab and let the component handle the route internally
    if (isInternalRoute && parentBasePath) {
      const parentTabId = parentBasePath;
      const existingParentTab = this.tabs.find((t) => t.id === parentTabId);

      if (existingParentTab) {
        // Just activate the parent tab - the component will handle the URL params
        if (this.activeTabId !== parentTabId) {
          this.activeTabId = parentTabId;
          this.emitChanges();
          this.saveToStorage();
        }
        return;
      } else {
        // Create the parent tab, component will handle the params from URL
        this.openParentTab(parentBasePath, config, {});
        return;
      }
    }

    // Generate tab ID (includes params for uniqueness)
    const tabId = this.generateTabId(url);

    // Check if tab already exists
    const existingTab = this.tabs.find((t) => t.id === tabId);
    if (existingTab) {
      this.recordHistory(existingTab);
      this.activateTab(tabId);
    } else {
      // Check if this is a child route
      if (config.isChildRoute && config.parentRoute) {
        this.openChildTab(url, config, params);
      } else {
        this.openParentTab(url, config, params);
      }
    }
  }

  /**
   * Parse URL to extract base path and parameters
   */
  private parseUrl(url: string): { basePath: string; params: Record<string, string>; isInternalRoute: boolean; parentBasePath: string | null } {
    // Remove query string
    const [path] = url.split('?');
    const segments = path.split('/').filter(Boolean);

    // For parameterized routes like /dashboard/patients/patient-record/:email
    // We need to identify which part is the route and which is the param
    const params: Record<string, string> = {};
    let basePath = path;
    let isInternalRoute = false;
    let parentBasePath: string | null = null;

    // Check if this is a parameterized route (e.g., patient-record/:email)
    // Pattern: /dashboard/{section}/{route}/{param} or /dashboard/{section}/{route}/{subroute}/{param}
    if (segments.length >= 3 && segments[0] === 'dashboard') {
      // Try 4-segment pattern first (e.g., /dashboard/patients/subscriptions/webhook/:id)
      if (segments.length >= 5) {
        const fourSegmentPattern = `/${segments.slice(0, 4).join('/')}/:id`;
        const fourSegmentPatternEmail = `/${segments.slice(0, 4).join('/')}/:email`;

        if (ROUTE_TAB_CONFIG[fourSegmentPattern] || ROUTE_TAB_CONFIG[fourSegmentPatternEmail]) {
          basePath = ROUTE_TAB_CONFIG[fourSegmentPattern] ? fourSegmentPattern : fourSegmentPatternEmail;
          const possibleParam = segments[4];
          params.id = possibleParam;

          if (possibleParam.includes('@') || decodeURIComponent(possibleParam).includes('@')) {
            params.email = decodeURIComponent(possibleParam);
          }
          return { basePath, params, isInternalRoute, parentBasePath };
        }
      }

      // Try 3-segment pattern (e.g., /dashboard/patients/patient-record/:email)
      if (segments.length >= 4) {
        const threeSegmentPattern = `/${segments.slice(0, 3).join('/')}/:id`;
        const threeSegmentPatternEmail = `/${segments.slice(0, 3).join('/')}/:email`;

        if (ROUTE_TAB_CONFIG[threeSegmentPattern] || ROUTE_TAB_CONFIG[threeSegmentPatternEmail]) {
          basePath = ROUTE_TAB_CONFIG[threeSegmentPattern] ? threeSegmentPattern : threeSegmentPatternEmail;
          const possibleParam = segments[3];
          params.id = possibleParam;

          if (possibleParam.includes('@') || decodeURIComponent(possibleParam).includes('@')) {
            params.email = decodeURIComponent(possibleParam);
          }
          return { basePath, params, isInternalRoute, parentBasePath };
        }

        // Check if this is an internal route (parameterized route with no config, but parent has config)
        // This happens when the component handles the params internally (like patient-record/:email)
        const threeSegmentPath = `/${segments.slice(0, 3).join('/')}`;
        if (ROUTE_TAB_CONFIG[threeSegmentPath]) {
          // The parent route has a config, but the parameterized version doesn't
          // This means the component handles the params internally
          basePath = threeSegmentPath;
          isInternalRoute = true;
          parentBasePath = threeSegmentPath;
          const possibleParam = segments[3];
          params.id = possibleParam;

          if (possibleParam.includes('@') || decodeURIComponent(possibleParam).includes('@')) {
            params.email = decodeURIComponent(possibleParam);
          }
          return { basePath, params, isInternalRoute, parentBasePath };
        }
      }

      // Try 2-segment pattern (e.g., /dashboard/hooks/:id)
      if (segments.length >= 3) {
        const twoSegmentPattern = `/${segments.slice(0, 2).join('/')}/:id`;

        if (ROUTE_TAB_CONFIG[twoSegmentPattern]) {
          basePath = twoSegmentPattern;
          const possibleParam = segments[2];
          params.id = possibleParam;

          if (possibleParam.includes('@') || decodeURIComponent(possibleParam).includes('@')) {
            params.email = decodeURIComponent(possibleParam);
          }
          return { basePath, params, isInternalRoute, parentBasePath };
        }

        // Check if this is an internal route for 2-segment paths
        const twoSegmentPath = `/${segments.slice(0, 2).join('/')}`;
        if (ROUTE_TAB_CONFIG[twoSegmentPath] && !ROUTE_TAB_CONFIG[twoSegmentPattern]) {
          basePath = twoSegmentPath;
          isInternalRoute = true;
          parentBasePath = twoSegmentPath;
          const possibleParam = segments[2];
          params.id = possibleParam;

          if (possibleParam.includes('@') || decodeURIComponent(possibleParam).includes('@')) {
            params.email = decodeURIComponent(possibleParam);
          }
          return { basePath, params, isInternalRoute, parentBasePath };
        }
      }

      // Try exact match on 3-segment path (parent routes)
      const threeSegmentPath = `/${segments.slice(0, 3).join('/')}`;
      if (ROUTE_TAB_CONFIG[threeSegmentPath]) {
        basePath = threeSegmentPath;
      }
    }

    return { basePath, params, isInternalRoute, parentBasePath };
  }

  /**
   * Get configuration for a route path
   */
  private getConfigForPath(path: string): typeof ROUTE_TAB_CONFIG[string] | null {
    return ROUTE_TAB_CONFIG[path] || null;
  }

  /**
   * Generate a unique tab ID from URL
   */
  private generateTabId(url: string): string {
    // Use the full URL path as the ID (without query params)
    const [path] = url.split('?');
    return path;
  }

  /**
   * Open a new parent tab
   */
  private openParentTab(
    url: string,
    config: typeof ROUTE_TAB_CONFIG[string],
    params: Record<string, string>
  ): void {
    const tabId = this.generateTabId(url);

    // Check if we have a pending display name from navigateWithDisplayName
    if (this.pendingDisplayName) {
      params.displayName = this.pendingDisplayName;
      this.pendingDisplayName = null;
    }

    const newTab: TabInfo = {
      id: tabId,
      title: config.title,
      route: url,
      icon: config.icon,
      closable: config.closable,
      dirty: false,
      params: Object.keys(params).length > 0 ? params : undefined,
      section: config.section,
      level: 'parent',
      childTabs: [],
    };

    this.tabs.push(newTab);
    this.activeTabId = tabId;

    this.recordHistory(newTab);
    this.emitChanges();
    this.saveToStorage();
  }

  /**
   * Open a new child tab within a parent tab context
   */
  private openChildTab(
    url: string,
    config: typeof ROUTE_TAB_CONFIG[string],
    params: Record<string, string>
  ): void {
    const tabId = this.generateTabId(url);
    const parentRoute = config.parentRoute!;

    // Check if we have a pending display name from navigateWithDisplayName
    if (this.pendingDisplayName) {
      params.displayName = this.pendingDisplayName;
      this.pendingDisplayName = null;
    }

    // Ensure parent tab exists
    const parentTab = this.ensureParentTab(parentRoute);

    const newChildTab: TabInfo = {
      id: tabId,
      title: config.title,
      route: url,
      icon: config.icon,
      closable: config.closable,
      dirty: false,
      params: Object.keys(params).length > 0 ? params : undefined,
      section: config.section,
      level: 'child',
      parentTabId: parentTab.id,
    };

    // Add child to parent's childTabs array
    if (!parentTab.childTabs) {
      parentTab.childTabs = [];
    }
    parentTab.childTabs.push(newChildTab);

    // Add to flat tabs array for routing and state management
    this.tabs.push(newChildTab);
    this.activeTabId = tabId;

    // Make sure parent tab is active in the main strip
    // (the child tab will be active in the child strip)

    this.recordHistory(newChildTab);
    this.emitChanges();
    this.saveToStorage();
  }

  /**
   * Ensure parent tab exists, create if needed
   */
  private ensureParentTab(parentRoute: string): TabInfo {
    // Look for existing parent tab
    let parentTab = this.tabs.find((t) => t.route === parentRoute && t.level === 'parent');

    if (!parentTab) {
      // Parent doesn't exist, create it
      const parentConfig = this.getConfigForPath(parentRoute);
      if (!parentConfig) {
        throw new Error(`No configuration found for parent route: ${parentRoute}`);
      }

      const parentTabId = this.generateTabId(parentRoute);
      parentTab = {
        id: parentTabId,
        title: parentConfig.title,
        route: parentRoute,
        icon: parentConfig.icon,
        closable: parentConfig.closable,
        dirty: false,
        section: parentConfig.section,
        level: 'parent',
        childTabs: [],
      };

      this.tabs.push(parentTab);
    }

    return parentTab;
  }

  /**
   * Navigate to a route and open it as a tab with a custom display name.
   * Use this when opening tabs for items that have a human-readable name.
   * @param route The route segments to navigate to
   * @param displayName The display name to show in the tab title
   * @param extras Optional NavigationExtras (e.g. queryParams)
   */
  navigateWithDisplayName(route: any[], displayName: string, extras?: NavigationExtras): void {
    // Store the display name temporarily for the next navigation
    this.pendingDisplayName = displayName;
    this.router.navigate(route, extras);
  }

  /**
   * Update the display name for the currently active tab.
   * Use this when an entity is loaded and you want to update the tab title.
   * @param displayName The new display name
   */
  updateActiveTabDisplayName(displayName: string): void {
    if (this.activeTabId && displayName) {
      const tab = this.tabs.find((t) => t.id === this.activeTabId);
      if (tab) {
        tab.params = tab.params || {};
        tab.params.displayName = displayName;
        this.pageHistory.updateDisplayName(tab.id, displayName);
        this.emitChanges();
        this.saveToStorage();
      }
    }
  }

  /** Temporary storage for display name during navigation */
  private pendingDisplayName: string | null = null;

  /**
   * Activate an existing tab
   */
  activateTab(tabId: string): void {
    const tab = this.tabs.find((t) => t.id === tabId);
    if (!tab) return;

    if (this.activeTabId !== tabId) {
      this.activeTabId = tabId;
      this.emitChanges();
      this.saveToStorage();
    }

    // If the router is already on this tab's route (e.g. a menu click already
    // navigated here), there is nothing to navigate to. Re-navigating would be a
    // no-op same-URL navigation that only widens the window for races. We only
    // navigate when the active route genuinely differs (tab-strip switch, restore
    // from storage).
    if (this.router.url === tab.route) {
      return;
    }

    this.router.navigateByUrl(tab.route).catch((err) => {
      console.error('[TabService] Navigation error:', err);
    });
  }

  /**
   * Close a tab
   * @returns Promise that resolves to true if tab was closed, false if cancelled
   */
  async closeTab(tabId: string, force = false): Promise<boolean> {
    const tabIndex = this.tabs.findIndex((t) => t.id === tabId);
    if (tabIndex === -1) {
      return false;
    }

    const tab = this.tabs[tabIndex];

    // Check for unsaved changes
    if (tab.dirty && !force) {
      const confirmed = await this.confirmCloseWithChanges();
      if (!confirmed) {
        return false;
      }
    }

    // If closing a parent tab, close all its children first
    if (tab.level === 'parent' && tab.childTabs && tab.childTabs.length > 0) {
      const childIds = tab.childTabs.map((c) => c.id);
      for (const childId of childIds) {
        await this.closeTab(childId, true);
      }
    }

    // If closing a child tab, remove it from parent's childTabs array
    if (tab.level === 'child' && tab.parentTabId) {
      const parentTab = this.tabs.find((t) => t.id === tab.parentTabId);
      if (parentTab && parentTab.childTabs) {
        const childIndex = parentTab.childTabs.findIndex((c) => c.id === tabId);
        if (childIndex !== -1) {
          parentTab.childTabs.splice(childIndex, 1);
        }
      }
    }

    // Remove the tab
    this.tabs.splice(tabIndex, 1);

    // If this was the active tab, activate another
    if (this.activeTabId === tabId) {
      if (this.tabs.length > 0) {
        // Determine next tab to activate
        let nextTab: TabInfo | null = null;

        // If we closed a child, activate another child from the same parent or the parent itself
        if (tab.level === 'child' && tab.parentTabId) {
          const parentTab = this.tabs.find((t) => t.id === tab.parentTabId);
          if (parentTab) {
            // Try to activate another child from the same parent
            if (parentTab.childTabs && parentTab.childTabs.length > 0) {
              nextTab = parentTab.childTabs[parentTab.childTabs.length - 1];
            } else {
              // No more children, activate the parent
              nextTab = parentTab;
            }
          }
        }

        // If we closed a parent or couldn't find a child, activate the next parent tab
        if (!nextTab) {
          const parentTabs = this.getParentTabs();
          if (parentTabs.length > 0) {
            const index = Math.min(tabIndex, parentTabs.length - 1);
            nextTab = parentTabs[index];
          }
        }

        if (nextTab) {
          this.activeTabId = nextTab.id;
          // Navigate to the new active tab
          this.router.navigateByUrl(nextTab.route).catch((err) => {
            console.error('[TabService] Navigation error on close:', err);
          });
        }
      } else {
        this.activeTabId = null;
        // Navigate to default route when no tabs are open
        this.router.navigateByUrl(DEFAULT_ROUTE).catch(err => {
          console.error('[TabService] Failed to navigate to default route:', err);
        });
      }
    }

    this.emitChanges();
    this.saveToStorage();
    return true;
  }

  /**
   * Close all tabs except the specified one
   */
  async closeOtherTabs(keepTabId: string): Promise<void> {
    const tabsToClose = this.tabs.filter((t) => t.id !== keepTabId && t.closable);

    for (const tab of tabsToClose) {
      await this.closeTab(tab.id, true);
    }
  }

  /**
   * Close all closable tabs
   */
  async closeAllTabs(): Promise<void> {
    const closableTabs = this.tabs.filter((t) => t.closable);

    for (const tab of closableTabs) {
      await this.closeTab(tab.id, true);
    }
  }

  /**
   * Close all tabs to the left of the specified tab
   */
  async closeTabsToLeft(tabId: string): Promise<void> {
    const tabIndex = this.tabs.findIndex((t) => t.id === tabId);
    if (tabIndex <= 0) return;

    const tabsToClose = this.tabs.slice(0, tabIndex).filter((t) => t.closable);

    for (const tab of tabsToClose) {
      await this.closeTab(tab.id, true);
    }
  }

  /**
   * Close all tabs to the right of the specified tab
   */
  async closeTabsToRight(tabId: string): Promise<void> {
    const tabIndex = this.tabs.findIndex((t) => t.id === tabId);
    if (tabIndex === -1 || tabIndex >= this.tabs.length - 1) return;

    const tabsToClose = this.tabs.slice(tabIndex + 1).filter((t) => t.closable);

    for (const tab of tabsToClose) {
      await this.closeTab(tab.id, true);
    }
  }

  /**
   * Set a tab's dirty state
   */
  setTabDirty(tabId: string, dirty: boolean): void {
    const tab = this.tabs.find((t) => t.id === tabId);
    if (tab && tab.dirty !== dirty) {
      tab.dirty = dirty;
      this.tabDirtyChanged.emit({ tabId, dirty });
      this.saveToStorage();
    }
  }

  /**
   * Set dirty state for the currently active tab
   */
  setActiveTabDirty(dirty: boolean): void {
    if (this.activeTabId) {
      this.setTabDirty(this.activeTabId, dirty);
    }
  }

  /**
   * Check if any tab has unsaved changes
   */
  hasUnsavedChanges(): boolean {
    return this.tabs.some((t) => t.dirty);
  }

  /**
   * Get the currently active tab
   */
  getActiveTab(): TabInfo | null {
    return this.tabs.find((t) => t.id === this.activeTabId) || null;
  }

  /**
   * Get the index of a tab by ID
   */
  getTabIndex(tabId: string): number {
    return this.tabs.findIndex((t) => t.id === tabId);
  }

  /**
   * Get the index of the active tab
   */
  getActiveTabIndex(): number {
    if (!this.activeTabId) return -1;
    return this.getTabIndex(this.activeTabId);
  }

  /**
   * Get all parent-level tabs only
   */
  getParentTabs(): TabInfo[] {
    return this.tabs.filter((t) => t.level === 'parent');
  }

  /**
   * Get child tabs for a specific parent tab
   */
  getChildTabs(parentTabId: string): TabInfo[] {
    const parentTab = this.tabs.find((t) => t.id === parentTabId);
    return parentTab?.childTabs || [];
  }

  /**
   * Get the parent tab for a given child tab
   */
  getParentTab(childTabId: string): TabInfo | null {
    const childTab = this.tabs.find((t) => t.id === childTabId);
    if (!childTab || !childTab.parentTabId) return null;
    return this.tabs.find((t) => t.id === childTab.parentTabId) || null;
  }

  /**
   * Get the active parent tab (either the active tab itself if it's a parent, or its parent if it's a child)
   */
  getActiveParentTab(): TabInfo | null {
    const activeTab = this.getActiveTab();
    if (!activeTab) return null;

    if (activeTab.level === 'parent') {
      return activeTab;
    } else {
      return this.getParentTab(activeTab.id);
    }
  }

  /**
   * Confirm closing a tab with unsaved changes
   */
  private confirmCloseWithChanges(): Promise<boolean> {
    // Using native confirm for now - can be replaced with a modal
    return Promise.resolve(
      confirm('This tab has unsaved changes. Are you sure you want to close it?')
    );
  }

  /**
   * Record a tab interaction into the persistent page history.
   */
  private recordHistory(tab: TabInfo): void {
    this.pageHistory.record({
      id: tab.id,
      title: tab.title,
      route: tab.route,
      icon: tab.icon,
      section: tab.section,
      params: tab.params,
      level: tab.level,
    });
  }

  /**
   * Emit change events
   */
  private emitChanges(): void {
    this.tabsChanged.emit([...this.tabs]);
    this.activeTabChanged.emit(this.activeTabId);
  }

  /**
   * Save current state to localStorage
   */
  private saveToStorage(): void {
    const state: TabPersistenceState = {
      tabs: this.tabs.map((t) => ({ ...t, dirty: false })), // Don't persist dirty state
      activeTabId: this.activeTabId,
      savedAt: Date.now(),
    };
    localStorage.setItem(TAB_STORAGE_KEY, JSON.stringify(state));
  }

  /**
   * Load state from localStorage
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(TAB_STORAGE_KEY);
      if (stored) {
        const state: TabPersistenceState = JSON.parse(stored);

        // Validate and restore tabs
        this.tabs = state.tabs.filter((t) => this.getConfigForPath(this.parseUrl(t.route).basePath));
        this.activeTabId = state.activeTabId;

        // Verify active tab exists
        if (this.activeTabId && !this.tabs.find((t) => t.id === this.activeTabId)) {
          this.activeTabId = this.tabs.length > 0 ? this.tabs[0].id : null;
        }
      }
    } catch (error) {
      console.warn('Failed to load tab state from storage:', error);
      this.tabs = [];
      this.activeTabId = null;
    }
  }

  /**
   * Clear persisted state
   */
  clearStorage(): void {
    localStorage.removeItem(TAB_STORAGE_KEY);
  }
}
